import { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Lock, User as UserIcon, Loader2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useBranding } from "@/contexts/BrandingContext";
import { toImageDataUrl } from "@/lib/image";

// Show splash only on the very first visit of the session
const SPLASH_KEY = "vidoc_splash_shown";

export default function Login() {
  const { user, login } = useAuth();
  const branding = useBranding();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Splash : show on first session entry only
  const [showSplash, setShowSplash] = useState(() => {
    try {
      return sessionStorage.getItem(SPLASH_KEY) !== "1";
    } catch (e) {
      // sessionStorage may be unavailable (private mode); default to showing the splash.
      console.warn("[Login] sessionStorage read failed", e?.name);
      return true;
    }
  });

  useEffect(() => {
    if (!showSplash) return;
    try {
      sessionStorage.setItem(SPLASH_KEY, "1");
    } catch (e) {
      console.warn("[Login] sessionStorage write failed", e?.name);
    }
    const t = setTimeout(() => setShowSplash(false), 2000);
    return () => clearTimeout(t);
  }, [showSplash]);

  if (user) return <Navigate to="/delivrance" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(username.trim(), password);
      navigate("/delivrance", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (detail === "Username not found") {
        setError("Utilisateur inconnu.");
      } else if (detail === "Wrong password") {
        setError("Mot de passe incorrect.");
      } else if (detail === "Account pending validation") {
        setError("Compte non validé. Contactez un administrateur.");
      } else {
        setError("Connexion impossible. Réessayez.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Use the auction house logo when available; only fall back to the
  // default PWA icon if the admin hasn't uploaded a custom one.
  const logoSrc = branding.icon_base64
    ? toImageDataUrl(branding.icon_base64)
    : "/icons/icon-192.png";
  const hasCustomLogo = Boolean(branding.icon_base64);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--vidoc-bg-secondary)] px-4 relative overflow-hidden">
      {showSplash && (
        <div className="splash-overlay" data-testid="splash-screen" aria-hidden="true">
          <img src={logoSrc} alt="" className="splash-logo" />
          <div className="splash-title">{branding.company_name || "VIDOC"}</div>
          <div className="splash-subtitle">Gestion des documents client</div>
        </div>
      )}

      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-[var(--vidoc-border)] p-8 animate-cardIn">
        <div className="text-center mb-8">
          <img
            src={logoSrc}
            alt={branding.company_name || "VIDOC"}
            className={`w-36 h-36 mx-auto ${hasCustomLogo ? "" : "rounded-2xl mb-4"} object-contain bg-white animate-logoIn`}
          />
          {/* When the auction house has uploaded its own logo, show its name
              as the headline and keep "VIDOC" as the smaller app label below.
              When no custom logo is configured we keep VIDOC as the main
              title (legacy / dev mode). */}
          {hasCustomLogo ? (
            <>
              {/* "VIDOC" — same heavy black weight, justified edge-to-edge
                  on the EXACT width of the logo above (w-36 = 144px).
                  +1px gap below the logo frame (visually flush). */}
              <div
                data-testid="login-app-name"
                className="mx-auto mt-px w-36 flex justify-between text-3xl font-black text-[var(--vidoc-primary)] leading-none"
              >
                <span>V</span>
                <span>I</span>
                <span>D</span>
                <span>O</span>
                <span>C</span>
              </div>
            </>
          ) : (
            <h1 className="text-3xl font-bold tracking-tight text-[var(--vidoc-primary)]">VIDOC</h1>
          )}
          <p className="text-sm text-[var(--vidoc-text-secondary)] mt-1">
            Gestion des documents client
          </p>
          <p className="text-sm text-[var(--vidoc-text-secondary)]">
            Maison de vente aux enchères
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
          <Field
            icon={UserIcon}
            type="text"
            placeholder="Identifiant"
            value={username}
            onChange={setUsername}
            testid="login-username-input"
            autoComplete="username"
          />
          <Field
            icon={Lock}
            type="password"
            placeholder="Mot de passe"
            value={password}
            onChange={setPassword}
            testid="login-password-input"
            autoComplete="current-password"
          />

          {error && (
            <div
              data-testid="login-error"
              className="px-3 py-2 rounded-lg bg-red-50 text-[var(--vidoc-danger)] text-sm"
            >
              {error}
            </div>
          )}

          <button
            data-testid="login-submit-btn"
            type="submit"
            disabled={loading || !username || !password}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[var(--vidoc-primary)] text-white font-semibold touch-target disabled:opacity-50 hover:bg-[var(--vidoc-primary-dark)] transition-colors"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Se connecter
          </button>
        </form>

        <p className="mt-6 text-xs text-center text-[var(--vidoc-text-tertiary)]">
          Application installable (PWA). Ajoutez-la à votre écran d'accueil pour
          un usage hors-ligne.
        </p>

        <p
          data-testid="login-copyright"
          className="mt-4 text-[11px] text-center text-[var(--vidoc-text-tertiary)]/80 select-none"
        >
          © François Meyniel · Paris · 2026
        </p>
        <p
          data-testid="login-version"
          className="mt-1 text-[11px] text-center text-[var(--vidoc-text-tertiary)]/80 select-none"
        >
          version 1.4
        </p>
      </div>
    </div>
  );
}

function Field({ icon: Icon, type, placeholder, value, onChange, testid, autoComplete }) {
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === "password";
  // When isPassword we toggle the actual input type so the user can read
  // what they typed (helps a lot on mobile where keystrokes are blind).
  const effectiveType = isPassword && showPassword ? "text" : type;

  return (
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--vidoc-text-tertiary)]" />
      <input
        data-testid={testid}
        type={effectiveType}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className={`w-full pl-10 ${isPassword ? "pr-12" : "pr-3"} py-3 rounded-xl bg-[var(--vidoc-bg-secondary)] border border-transparent focus:border-[var(--vidoc-primary)] focus:bg-white outline-none touch-target`}
      />
      {isPassword && (
        <button
          type="button"
          data-testid={`${testid}-toggle`}
          aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
          onClick={() => setShowPassword((v) => !v)}
          tabIndex={-1}
          className="absolute inset-y-0 right-0 px-3 flex items-center text-[var(--vidoc-text-tertiary)] hover:text-[var(--vidoc-text)]"
        >
          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      )}
    </div>
  );
}
